import { AxiosResponse } from 'axios';
import { Course, Report, Step } from '../models/course';
import cacheableRequest from '../../../services/cacheableRequest';

type Id = {
  id: string;
};

type ProgressStatus = 'not_started' | 'in_progress' | 'complete' | 'completed';

type TaskListResponse = {
  _id: Id;
  id?: string;
  originalTaskListId: Id;
  name: string;
  assigneeId: Id;
  assignorId: Id;
  assignedUser?: string;
  status: ProgressStatus;
  tasks: Step[];
  hideCompleted: boolean;
  expanded: boolean;
  reports: Report[];
  active: boolean;
  timeAssigned: string;
  timeStarted?: string;
  timeCompleted?: string;
  unAssigned: boolean;
  selfAssigned: boolean;
  description?: string;
  assignorOrgName: string;
  discussionDisabled: boolean;
  progress?: number;
  purchaseRequired?: { value: boolean | null };
  dueDate?: string;
  certificateInfo?: unknown;
  posterImage?: string;
};

const calculateProgress = (tasks: Step[]): number => {
  if (!tasks || tasks.length === 0) {
    return 0;
  }

  const completedCount = tasks.filter(task => task.status === 'complete').length;
  return completedCount / tasks.length;
};

const transformTaskListInstance = (response: TaskListResponse): Course | null => {
  if (!response?._id) {
    return null;
  }

  const firstReport = response.reports?.[0];

  return {
    id: response._id.id,
    originalTaskListId: response.originalTaskListId?.id ?? '',
    name: response.name,
    description: response.description,
    assignedUser: response.assigneeId?.id ?? '',
    assigningOrg: response.assignorId?.id ?? '',
    status: response.status,
    tasks: response.tasks.sort((a, b) => (a.task?.order || 0) - (b.task?.order || 0)),
    hideCompleted: response.hideCompleted,
    activePanel: response.expanded ? 0 : -1,
    expanded: response.expanded,
    progress: response.progress ?? calculateProgress(response.tasks),
    reports: response.reports ?? [],
    reportId: firstReport?.reportId,
    reportToGenerate: firstReport?.id,
    timeAssigned: parseInt(response.timeAssigned ?? '', 10) ?? 0,
    timeStarted: parseInt(response.timeStarted ?? '', 10) ?? 0,
    timeCompleted: parseInt(response.timeCompleted ?? '', 10) ?? 0,
    purchaseRequired: response.purchaseRequired?.value ?? null,
    dueDate: response.dueDate,
    ownerName: response.assignorOrgName,
    selfAssigned: response.selfAssigned,
    certificateInfo: response.certificateInfo ?? null,
    discussionDisabled: response.discussionDisabled === true,
    posterImage: response.posterImage,
  };
};

export const getTasklistInstance = (
  tasklistInstanceId?: string,
  bypassCaching: boolean = false
): Promise<Course | null> => {
  return cacheableRequest(`/v1/user-tasklists/${tasklistInstanceId}`, 5, bypassCaching).then(
    ({ data }: AxiosResponse<TaskListResponse>) => {
      return transformTaskListInstance(data);
    }
  );
};
